export interface CatalogAttribute {
  AttributeKey: string;
  AttributeName?: string | null;
  TechnicalName?: string | null;
}

export interface ProductAttribute {
  AttributeKey: string;
  AttributeName?: string | null;
  TechnicalName?: string | null;
  Value?: string | null;
  Unit?: string | null;
  ValueList?: string[] | null;
}

/**
 * Builds the per-product attribute rows for the product list table, keyed by product ID.
 *
 * Every product is padded to the same column layout as the catalog definition (missing attributes
 * become empty placeholders) so that all rows line up under the shared table head; attributes a
 * product carries beyond the catalog are appended afterwards.
 *
 * `records` must be the *accumulated* product records of all loaded pages, not a single page's
 * query result - the rendered rows outlive the query result they arrived in, and a row whose ID is
 * missing from this map renders no attributes at all.
 */
const padAttributes = (
  records: any[],
  catalogAttributes: CatalogAttribute[] | undefined,
): Record<string, ProductAttribute[]> => {
  const attributesPerRecord: Record<string, ProductAttribute[]> = {};

  (records || []).forEach((record: any) => {
    if (!record?.catalogProductData) {
      console.error('ignoring record', record);
      return;
    }

    const productAttributes: ProductAttribute[] = [
      ...(record.catalogProductData.Attributes || []),
      ...(record.catalogProductData.LogisticAttributes || []),
    ];

    const attributes: ProductAttribute[] = [];
    let index = 0;
    const seen = new Set<string>();

    catalogAttributes?.forEach((catalogAttribute) => {
      seen.add(catalogAttribute.AttributeKey);
      if (productAttributes[index]?.AttributeKey === catalogAttribute.AttributeKey) {
        attributes.push(productAttributes[index]);
        index += 1;
      } else {
        const searchedIndex = productAttributes.findIndex(
          (attribute) => attribute.AttributeKey === catalogAttribute.AttributeKey,
        );
        if (searchedIndex >= 0) {
          attributes.push(productAttributes[searchedIndex]);
          index = searchedIndex + 1;
        } else {
          attributes.push({
            AttributeKey: catalogAttribute.AttributeKey,
            AttributeName: catalogAttribute.AttributeName,
            TechnicalName: catalogAttribute.TechnicalName,
            Value: '',
          });
        }
      }
    });

    productAttributes.forEach((productAttribute) => {
      if (!seen.has(productAttribute.AttributeKey)) {
        attributes.push(productAttribute);
      }
    });

    attributesPerRecord[record.ID] = attributes;
  });

  return attributesPerRecord;
};

export default padAttributes;
